import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { api, API } from "@/lib/api";
import { PageHeader, Loader } from "@/components/portal/Shared";
import { EvidenceBadge } from "@/components/portal/EvidenceBadge";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { User, UserPlus, Camera, Pencil, Trash2, Loader2, TreeDeciduous, Mars, Venus, Download, Image as ImageIcon, Move, Share2, RotateCcw, Copy, Check, Link2, ExternalLink } from "lucide-react";

const NODE_W = 150, COL_W = 200, ROW_H = 230, PAD_X = 48, PAD_Y = 24, AVATAR = 88, NODE_H = 184;

const imgSrc = (u) => (!u ? null : (u.startsWith("http") ? u : `${API.replace(/\/api$/, "")}${u}`));

export const GenderBadge = ({ gender }) => {
  if (gender === "male") return <span className="inline-grid place-items-center h-4 w-4 rounded-full bg-[#2563EB] text-white" title="Male"><Mars size={11} /></span>;
  if (gender === "female") return <span className="inline-grid place-items-center h-4 w-4 rounded-full bg-[#DB2777] text-white" title="Female"><Venus size={11} /></span>;
  return null;
};

export function Avatar({ node, size = 88 }) {
  const src = imgSrc(node.photo_url);
  return (
    <div className="rounded-full p-[3px]" style={{ width: size, height: size, background: "linear-gradient(135deg,#E7C965,#D4AF37)" }}>
      <div className="h-full w-full rounded-full p-[3px] bg-[#FBF7EF]">
        <div className="h-full w-full rounded-full overflow-hidden bg-[#EDE4D3] grid place-items-center">
          {src ? <img src={src} alt={node.name} draggable={false} className="h-full w-full object-cover" /> : <User size={size * 0.42} className="text-[#B49A6A]" />}
        </div>
      </div>
    </div>
  );
}

const BLANK = { name: "", relation: "", birth: "", death: "", place: "", gender: "", note: "", link_id: "", link_mode: "child", spouse_id: "" };

function MemberForm({ initial, members, mode, selfId, submitting, onSubmit }) {
  const [f, setF] = useState({ ...BLANK, ...initial });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const inputCls = "w-full rounded-lg border border-[#E4DCD3] bg-white px-3 py-2.5 text-sm focus:border-[#59288E] outline-none";
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">Full name *</label>
        <input data-testid="member-name-input" value={f.name} onChange={set("name")} className={inputCls} placeholder="e.g. Amos Johnson" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">Relationship</label>
          <input data-testid="member-relation-input" value={f.relation} onChange={set("relation")} className={inputCls} placeholder="e.g. great-grandfather" />
        </div>
        <div>
          <label className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">Gender</label>
          <select data-testid="member-gender-select" value={f.gender} onChange={set("gender")} className={inputCls}>
            <option value="">Unspecified</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">Born (year)</label>
          <input data-testid="member-birth-input" value={f.birth} onChange={set("birth")} className={inputCls} placeholder="1846" />
        </div>
        <div>
          <label className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">Died (year)</label>
          <input data-testid="member-death-input" value={f.death} onChange={set("death")} className={inputCls} placeholder="1911" />
        </div>
      </div>
      <div>
        <label className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">Place</label>
        <input data-testid="member-place-input" value={f.place} onChange={set("place")} className={inputCls} placeholder="Kemper County, Mississippi" />
      </div>
      <div>
        <label className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">Notes</label>
        <textarea data-testid="member-note-input" value={f.note} onChange={set("note")} rows={2} className={inputCls} placeholder="Evidence, sources, family stories…" />
      </div>
      {mode === "add" && (
        <div className="rounded-xl bg-[#F3EEFA] p-3 space-y-3">
          <div className="text-xs uppercase font-mono tracking-wider text-[#59288E]">Connect to the tree</div>
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="member-linkmode-select" value={f.link_mode} onChange={set("link_mode")} className={inputCls}>
              <option value="child">Is the child of…</option>
              <option value="parent">Is the parent of…</option>
              <option value="spouse">Is the spouse of…</option>
              <option value="none">Not linked yet</option>
            </select>
            <select data-testid="member-link-select" value={f.link_id} onChange={set("link_id")} disabled={f.link_mode === "none"} className={inputCls + (f.link_mode === "none" ? " opacity-50" : "")}>
              <option value="">Select a member…</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        </div>
      )}
      {mode === "edit" && (
        <div className="rounded-xl bg-[#F3EEFA] p-3">
          <label className="text-xs uppercase font-mono tracking-wider text-[#59288E]">Spouse (joins them side-by-side)</label>
          <select data-testid="member-spouse-select" value={f.spouse_id || ""} onChange={set("spouse_id")} className={inputCls + " mt-1"}>
            <option value="">No spouse</option>
            {members.filter((m) => m.id !== selfId).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      )}
      <button data-testid="member-save-btn" disabled={submitting || !f.name.trim()} onClick={() => onSubmit(f)}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2A1442] text-white px-6 py-3 text-sm font-semibold hover:bg-[#482276] transition-colors disabled:opacity-60">
        {submitting ? <Loader2 size={16} className="animate-spin" /> : (mode === "add" ? <UserPlus size={16} className="text-[#E7C965]" /> : <Pencil size={16} className="text-[#E7C965]" />)}
        {mode === "add" ? "Add to family tree" : "Save changes"}
      </button>
    </div>
  );
}

function orderGen(list) {
  const byId = Object.fromEntries(list.map((n) => [n.id, n]));
  const used = new Set(); const ordered = [];
  list.forEach((n) => {
    if (used.has(n.id)) return;
    ordered.push(n); used.add(n.id);
    if (n.spouse && byId[n.spouse] && !used.has(n.spouse)) { ordered.push(byId[n.spouse]); used.add(n.spouse); }
  });
  return ordered;
}

export function computeDefaults(nodes) {
  const byGen = {};
  nodes.forEach((n) => { (byGen[n.generation] = byGen[n.generation] || []).push(n); });
  const gens = Object.keys(byGen).map(Number).sort((a, b) => b - a);
  const rows = gens.map((g) => orderGen(byGen[g]));
  const maxCols = Math.max(1, ...rows.map((r) => r.length));
  const defaultW = PAD_X * 2 + maxCols * COL_W;
  const pos = {};
  const rowY = {};
  rows.forEach((row, ri) => {
    const rowW = row.length * COL_W;
    const startX = (defaultW - rowW) / 2 + (COL_W - NODE_W) / 2;
    rowY[gens[ri]] = PAD_Y + ri * ROW_H;
    row.forEach((n, ci) => {
      const saved = n.pos && typeof n.pos.x === "number" ? n.pos : null;
      pos[n.id] = saved || { x: startX + ci * COL_W, y: PAD_Y + ri * ROW_H };
    });
  });
  return { pos, defaultW, defaultH: PAD_Y * 2 + rows.length * ROW_H, gens, rowY };
}

export { NODE_W, NODE_H, AVATAR, PAD_X, PAD_Y };

export function computeConnectors(nodes, positions) {
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const byName = {}; nodes.forEach((n) => { byName[n.name] = n; });
  const cx = (id) => (positions[id]?.x ?? 0) + NODE_W / 2;
  const topY = (id) => positions[id]?.y ?? 0;
  const botY = (id) => (positions[id]?.y ?? 0) + AVATAR;
  const midY = (id) => (positions[id]?.y ?? 0) + AVATAR / 2;
  const childPaths = [], spousePaths = [];
  nodes.forEach((n) => {
    if (n.parent && byName[n.parent] && positions[n.id] && positions[byName[n.parent].id]) {
      const p = byName[n.parent];
      let sx = cx(p.id), sy = botY(p.id);
      if (p.spouse && positions[p.spouse]) { sx = (cx(p.id) + cx(p.spouse)) / 2; sy = Math.max(midY(p.id), midY(p.spouse)); }
      const ex = cx(n.id), ey = topY(n.id);
      const my = (sy + ey) / 2;
      childPaths.push({ key: n.id, d: `M ${sx} ${sy} V ${my} H ${ex} V ${ey}` });
    }
    if (n.spouse && byId[n.spouse] && positions[n.id] && positions[n.spouse] && n.id < n.spouse) {
      const a = cx(n.id) <= cx(n.spouse) ? n.id : n.spouse;
      const b = a === n.id ? n.spouse : n.id;
      spousePaths.push({ key: `${n.id}-${n.spouse}`, x1: cx(a) + AVATAR / 2, x2: cx(b) - AVATAR / 2, y: midY(a) });
    }
  });
  return { childPaths, spousePaths };
}

export default function FamilyTree() {
  const [nodes, setNodes] = useState(null);
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [positions, setPositions] = useState({});
  const [shareOpen, setShareOpen] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const movedRef = useRef(false);

  const load = useCallback(() => api.get("/family-tree").then((r) => setNodes(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const meta = useMemo(() => (nodes ? computeDefaults(nodes) : null), [nodes]);
  useEffect(() => { if (meta) setPositions(meta.pos); }, [meta]);

  const dims = useMemo(() => {
    let w = meta?.defaultW || 600, h = meta?.defaultH || 400;
    Object.values(positions).forEach((p) => { w = Math.max(w, p.x + NODE_W + PAD_X); h = Math.max(h, p.y + NODE_H + PAD_Y); });
    return { w, h };
  }, [positions, meta]);

  const { childPaths, spousePaths } = useMemo(() => (nodes ? computeConnectors(nodes, positions) : { childPaths: [], spousePaths: [] }), [nodes, positions]);

  const startDrag = (e, id) => {
    if (e.button === 2) return;
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
    const startX = e.clientX, startY = e.clientY;
    const orig = positions[id] || { x: 0, y: 0 };
    movedRef.current = false;
    const move = (ev) => {
      const dx = ev.clientX - startX, dy = ev.clientY - startY;
      if (Math.abs(dx) + Math.abs(dy) > 4) movedRef.current = true;
      setPositions((p) => ({ ...p, [id]: { x: Math.max(0, orig.x + dx), y: Math.max(0, orig.y + dy) } }));
    };
    const up = (ev) => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      if (movedRef.current) {
        const final = { x: Math.max(0, orig.x + (ev.clientX - startX)), y: Math.max(0, orig.y + (ev.clientY - startY)) };
        api.patch(`/ancestors/${id}`, { pos: final }).catch(() => toast.error("Couldn't save the new position"));
        setTimeout(() => { movedRef.current = false; }, 60);
      }
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  };

  const addMember = async (f) => {
    setBusy(true);
    try { await api.post("/ancestors", f); toast.success(`${f.name} added to your family tree`); setAdding(false); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add member"); }
    finally { setBusy(false); }
  };
  const saveEdit = async (f) => {
    setBusy(true);
    try { const { data } = await api.patch(`/ancestors/${selected.id}`, f); toast.success("Member updated"); setEditing(false); setSelected(data); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update member"); }
    finally { setBusy(false); }
  };
  const removeMember = async () => {
    if (!selected) return;
    setBusy(true);
    try { await api.delete(`/ancestors/${selected.id}`); toast.success(`${selected.name} removed`); setSelected(null); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove member"); }
    finally { setBusy(false); }
  };
  const uploadPhoto = async (file) => {
    if (!file || !selected) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post(`/ancestors/${selected.id}/photo`, fd); toast.success("Photo added"); setSelected(data); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not upload photo"); }
    finally { setUploading(false); }
  };

  const downloadBlob = (blob, name) => {
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };
  const resetLayout = async () => {
    const def = computeDefaults(nodes.map((n) => ({ ...n, pos: null })));
    setPositions(def.pos);
    await Promise.all(nodes.map((n) => api.patch(`/ancestors/${n.id}`, { pos: null }).catch(() => {})));
    toast.success("Layout reset to the auto-arrangement");
  };

  const openShare = async () => {
    setShareOpen(true); setShareBusy(true);
    try {
      let { data } = await api.get("/family-tree/share");
      if (!data.token) { const r = await api.post("/family-tree/share"); data = r.data; }
      setShareToken(data.token);
    } catch { toast.error("Could not create a share link"); }
    finally { setShareBusy(false); }
  };
  const shareUrl = shareToken ? `${window.location.origin}/tree/${shareToken}` : "";
  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Could not copy the link"); }
  };
  const revokeShare = async () => {
    setShareBusy(true);
    try { await api.delete("/family-tree/share"); setShareToken(null); toast.success("Share link revoked"); }
    catch { toast.error("Could not revoke the link"); }
    finally { setShareBusy(false); }
  };

  const exportTree = async (fmt) => {
    if (!canvasRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(canvasRef.current, { backgroundColor: "#FBF7EF", scale: 2, useCORS: true, width: dims.w, height: dims.h, windowWidth: dims.w, windowHeight: dims.h });
      if (fmt === "png") {
        canvas.toBlob((blob) => downloadBlob(blob, "family-tree.png"), "image/png");
      } else {
        const img = canvas.toDataURL("image/jpeg", 0.92);
        const w = canvas.width / 2, h = canvas.height / 2;
        const pdf = new jsPDF({ orientation: w >= h ? "landscape" : "portrait", unit: "pt", format: [w, h] });
        pdf.addImage(img, "JPEG", 0, 0, w, h);
        pdf.save("family-tree.pdf");
      }
      toast.success("Family tree exported");
    } catch (e) { toast.error("Could not export the tree"); }
    finally { setExporting(false); }
  };

  if (!nodes) return <Loader />;

  return (
    <div data-testid="module-family-tree">
      <PageHeader
        eyebrow="Verify"
        title="Family Tree"
        subtitle="A living pedigree of your line. Add relatives, join couples, drag members to arrange the layout, and download the whole chart as a poster."
        right={
          <button data-testid="add-member-btn" onClick={() => setAdding(true)}
            className="inline-flex items-center gap-2 rounded-full bg-[#2A1442] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#482276] transition-colors">
            <UserPlus size={16} className="text-[#E7C965]" /> Add Member
          </button>
        }
      />

      <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
        <div className="inline-flex items-center gap-3 rounded-2xl bg-[#2A1442] px-6 py-3">
          <TreeDeciduous size={24} className="text-[#7BB661]" />
          <div className="font-serif text-lg font-bold text-white tracking-wide">FAMILY TREE</div>
        </div>
        <button data-testid="export-tree-png-btn" onClick={() => exportTree("png")} disabled={exporting}
          className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#180D24] px-4 py-2.5 text-sm font-bold hover:bg-[#E7C965] transition-colors disabled:opacity-60">
          {exporting ? <Loader2 size={15} className="animate-spin" /> : <ImageIcon size={15} />} Download PNG
        </button>
        <button data-testid="export-tree-pdf-btn" onClick={() => exportTree("pdf")} disabled={exporting}
          className="inline-flex items-center gap-2 rounded-full border border-[#E4DCD3] bg-white px-4 py-2.5 text-sm font-semibold text-[#59288E] hover:border-[#D4AF37] transition-colors disabled:opacity-60">
          <Download size={15} /> Download PDF
        </button>
        <button data-testid="share-tree-btn" onClick={openShare}
          className="inline-flex items-center gap-2 rounded-full border border-[#E4DCD3] bg-white px-4 py-2.5 text-sm font-semibold text-[#59288E] hover:border-[#D4AF37] transition-colors">
          <Share2 size={15} /> Share
        </button>
        <button data-testid="reset-layout-btn" onClick={resetLayout}
          className="inline-flex items-center gap-2 rounded-full border border-[#E4DCD3] bg-white px-4 py-2.5 text-sm font-semibold text-[#59288E] hover:border-[#D4AF37] transition-colors">
          <RotateCcw size={15} /> Reset layout
        </button>
        <span className="inline-flex items-center gap-1.5 text-xs text-[#8F7220] font-mono"><Move size={13} /> Tip: drag any member to rearrange</span>
      </div>

      <div className="overflow-auto rounded-3xl border border-[#E4DCD3] bg-[#FBF7EF]" data-testid="family-tree-scroll">
        <div ref={canvasRef} className="relative" style={{ width: dims.w, height: dims.h, minWidth: "100%" }} data-testid="family-tree-canvas">
          <svg className="absolute inset-0 pointer-events-none" width={dims.w} height={dims.h} style={{ zIndex: 0 }}>
            {childPaths.map((p) => <path key={p.key} d={p.d} fill="none" stroke="#8F7220" strokeWidth="2" strokeDasharray="5 5" opacity="0.7" />)}
            {spousePaths.map((s) => <line key={s.key} x1={s.x1} y1={s.y} x2={s.x2} y2={s.y} stroke="#D4AF37" strokeWidth="3" />)}
          </svg>

          {meta?.gens.map((g) => (
            <div key={g} className="absolute text-[10px] uppercase font-mono tracking-[0.2em] text-[#C3B393]" style={{ left: 10, top: (meta.rowY[g] || 0) + 34 }}>Gen {g}</div>
          ))}

          {nodes.map((n) => {
            const p = positions[n.id] || { x: 0, y: 0 };
            return (
              <div
                key={n.id}
                data-testid={`tree-node-${n.id}`}
                onPointerDown={(e) => startDrag(e, n.id)}
                onClick={() => { if (movedRef.current) return; setSelected(n); setEditing(false); }}
                style={{ position: "absolute", left: p.x, top: p.y, width: NODE_W, zIndex: 1 }}
                className="flex flex-col items-center text-center cursor-grab active:cursor-grabbing select-none group"
              >
                <Avatar node={n} />
                <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] text-[#180D24] px-3 py-1 text-[10px] font-bold uppercase tracking-wide max-w-full">
                  <GenderBadge gender={n.gender} />
                  <span className="truncate">{n.relation || "Relative"}</span>
                </div>
                <div className="mt-1.5 font-serif text-sm font-semibold text-[#180D24] leading-tight">{n.name}</div>
                <div className="text-[11px] text-[#8F7220] font-mono">{n.birth || "?"}{n.death ? `–${n.death}` : ""}</div>
                <div className="mt-1"><EvidenceBadge status={n.evidence_status} size="sm" /></div>
              </div>
            );
          })}

          <div className="absolute text-[10px] font-mono text-[#B49A6A]" style={{ left: 16, top: dims.h - 22 }}>WhatsMyTribe.com — Powered by ISelfLawAmMaster.com</div>
        </div>
      </div>

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md" data-testid="share-tree-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#180D24]">Share your family tree</DialogTitle></DialogHeader>
          <p className="text-sm text-[#756D7E]">Anyone with this private link can view your tree online (read-only). Photos and details are visible — revoke it any time.</p>
          {shareBusy && !shareToken ? (
            <div className="py-8 text-center"><Loader2 size={22} className="animate-spin text-[#59288E] mx-auto" /></div>
          ) : shareToken ? (
            <>
              <div className="mt-3 flex items-center gap-2 rounded-xl border border-[#E4DCD3] bg-[#FAF8F5] p-2">
                <Link2 size={16} className="text-[#8F7220] shrink-0 ml-1" />
                <input data-testid="share-link-input" readOnly value={shareUrl} className="flex-1 bg-transparent text-sm text-[#180D24] outline-none truncate" />
                <button data-testid="copy-share-link-btn" onClick={copyShare}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#D4AF37] text-[#180D24] px-3 py-1.5 text-xs font-bold hover:bg-[#E7C965] transition-colors">
                  {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <a data-testid="open-share-link" href={shareUrl} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#E4DCD3] px-4 py-2 text-sm font-semibold text-[#59288E] hover:border-[#D4AF37] transition-colors">
                  <ExternalLink size={14} /> Open link
                </a>
                <button data-testid="revoke-share-btn" onClick={revokeShare} disabled={shareBusy}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#F1C0C0] px-4 py-2 text-sm font-semibold text-[#B91C1C] hover:bg-[#FDF0F0] transition-colors disabled:opacity-60">
                  <Trash2 size={14} /> Revoke link
                </button>
              </div>
            </>
          ) : (
            <div className="py-6 text-center text-sm text-[#756D7E]">No active link.</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add member dialog */}
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="add-member-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#180D24]">Add a family member</DialogTitle></DialogHeader>
          <MemberForm mode="add" members={nodes} submitting={busy} onSubmit={addMember} />
        </DialogContent>
      </Dialog>

      {/* Detail / edit drawer */}
      <Drawer open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setEditing(false); } }}>
        <DrawerContent>
          {selected && (
            <div className="max-w-2xl mx-auto w-full p-6 pb-10">
              {!editing ? (
                <>
                  <div className="flex items-center gap-4 mb-4">
                    <Avatar node={selected} size={72} />
                    <div>
                      <DrawerHeader className="px-0 py-0">
                        <DrawerTitle className="font-serif text-2xl text-[#180D24]">{selected.name}</DrawerTitle>
                      </DrawerHeader>
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        <GenderBadge gender={selected.gender} />
                        <span className="text-sm text-[#756D7E]">{selected.relation}</span>
                        <EvidenceBadge status={selected.evidence_status} />
                      </div>
                    </div>
                  </div>

                  <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="node-photo-upload-input"
                    onChange={(e) => { if (e.target.files?.[0]) uploadPhoto(e.target.files[0]); e.target.value = ""; }} />
                  <div className="flex items-center gap-2 flex-wrap mb-4">
                    <button data-testid="node-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                      className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#180D24] px-4 py-2 text-sm font-bold hover:bg-[#E7C965] transition-colors disabled:opacity-60">
                      {uploading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} {selected.photo_url ? "Change photo" : "Add photo"}
                    </button>
                    <button data-testid="node-edit-btn" onClick={() => setEditing(true)}
                      className="inline-flex items-center gap-2 rounded-full border border-[#E4DCD3] px-4 py-2 text-sm font-semibold text-[#59288E] hover:border-[#D4AF37] transition-colors">
                      <Pencil size={15} /> Edit
                    </button>
                    <button data-testid="node-delete-btn" onClick={removeMember} disabled={busy}
                      className="inline-flex items-center gap-2 rounded-full border border-[#F1C0C0] px-4 py-2 text-sm font-semibold text-[#B91C1C] hover:bg-[#FDF0F0] transition-colors disabled:opacity-60">
                      <Trash2 size={15} /> Remove
                    </button>
                  </div>

                  <dl className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div><dt className="text-[#8F7220] font-mono text-xs uppercase">Born</dt><dd className="text-[#180D24]">{selected.birth || "Unknown"}</dd></div>
                    <div><dt className="text-[#8F7220] font-mono text-xs uppercase">Died</dt><dd className="text-[#180D24]">{selected.death || "Unknown"}</dd></div>
                    <div className="col-span-2"><dt className="text-[#8F7220] font-mono text-xs uppercase">Place</dt><dd className="text-[#180D24]">{selected.place || "Unknown"}</dd></div>
                    {selected.parent && <div><dt className="text-[#8F7220] font-mono text-xs uppercase">Child of</dt><dd className="text-[#180D24]">{selected.parent}</dd></div>}
                    {selected.spouse && <div><dt className="text-[#8F7220] font-mono text-xs uppercase">Spouse</dt><dd className="text-[#180D24]">{nodes.find((x) => x.id === selected.spouse)?.name || "—"}</dd></div>}
                  </dl>
                  {selected.note && (
                    <div className="rounded-xl bg-[#FAF8F5] border border-[#E4DCD3] p-4 text-sm text-[#1A171D]">
                      <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-1">Evidence Note</div>
                      {selected.note}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <DrawerHeader className="px-0"><DrawerTitle className="font-serif text-2xl text-[#180D24]">Edit {selected.name}</DrawerTitle></DrawerHeader>
                  <MemberForm mode="edit" members={nodes} selfId={selected.id} submitting={busy} onSubmit={saveEdit}
                    initial={{ name: selected.name, relation: selected.relation || "", birth: selected.birth || "", death: selected.death || "", place: selected.place || "", gender: selected.gender || "", note: selected.note || "", spouse_id: selected.spouse || "" }} />
                </>
              )}
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
}
